import { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Brand, Chip, Icon } from '@/components/kit';
import { DESIGN_ROLES, type DesignDoor, type DesignRoleId } from '@/config/designRoles';
import { useAuthStore } from '@/stores/useAuthStore';
import { doorRouteFor } from '@/pages/roles/registry';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { useRailSubs, railBadges } from '@/layouts/railSubs';
import type { SidebarItem } from '@/config/navigation';

/**
 * THE RAIL THE DESIGN DRAWS — this role's doors, not the product's menu.
 *
 * A Doctor gets FIVE: Today · Queue · Patients · Library · Directory, with the
 * clinic-manager hat folded beneath (§10's corrected multi-role law). The
 * product's navigation is twelve rows across six groups, and rendering those in
 * the kit's styling produces something that LOOKS like the design and is not
 * it — which is what this file did until now.
 *
 * WHERE THE OTHER DOORS WENT. The cut IS the redesign, not a loss: §Doctor 03
 * [1] is explicit that "the file, the encounter, the sheet and the results open
 * from a patient or from the day's chips — never from here." Everything absent
 * keeps its route and stays reachable through ⌘K and /explore, which exists to
 * be the full map. Nothing is deleted; the rail stops being the index.
 *
 * WHEN THE DESIGN HAS NO ANSWER — a role with no canvas surface, which is three
 * of the nine — this falls back to the product's own nav. Better a menu that is
 * honest about this clinic than a set of doors borrowed from a role the person
 * does not hold. That fallback is why `items` is still a parameter.
 *
 * THE CONTENT AREA IS NOT INSIDE `.maie`, and that is load-bearing: kit.css
 * opens with `.maie * { margin: 0; padding: 0 }`, so scoping a page to it would
 * strip the spacing off every un-ported screen. Only the chrome is in kit scope.
 */
export function MaieRail({
  items,
  onOpenMenu,
}: {
  /** The product's nav — the fallback for roles the design does not cover. */
  items: SidebarItem[];
  onOpenMenu: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { person, branch, surface: identitySurface, surfaceIsFallback } = useShellIdentity();
  /* The manager on the Directory wears the board's manager nav (Overview …
     Flows + Directory + the Doctor fold), not the System Settings list. */
  const surface: DesignRoleId =
    identitySurface === 'clinic-manager' && location.pathname.startsWith('/directory') &&
    !(location.state as { settingsFrame?: boolean } | null)?.settingsFrame
      ? 'directory'
      : identitySurface;
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const baseDef = DESIGN_ROLES[surface];
  /* g_reg_recep — on the Directory the desk's rail is seven bare doors, with
     Catalog beside Billing and the inbox just "Messages". */
  const bareDesk = surface === 'reception' && location.pathname.startsWith('/directory');
  const bareDoors: DesignDoor[] = [
          { key: 'queue', label: 'Queue', icon: 'layout-list', route: '/queue' },
          { key: 'schedule', label: 'Schedule', icon: 'calendar', route: '/schedule' },
          { key: 'patients', label: 'Patients', icon: 'users', route: '/patients' },
          { key: 'billing', label: 'Billing', icon: 'cash', route: '/billing' },
          { key: 'catalog', label: 'Catalog', icon: 'list-details', route: '/reception/billing/catalog' },
          { key: 'messages', label: 'Messages', icon: 'message-circle', route: '/inbox' },
          { key: 'directory', label: 'Directory', icon: 'address-book', route: '/directory' },
  ];
  const def = bareDesk ? { ...baseDef, doors: bareDoors } : baseDef;
  const anyRouted = def.doors.some((d) => Boolean(doorRouteFor(surface, d.key) ?? d.route));
  /* The canvas draws a count or a contents list under each of this role's
     doors; the lists are static beside the door, the counts are data. */
  const rawSubs = useRailSubs(surface);
  const liveSubs = bareDesk ? {} : rawSubs;
  const liveBadges = bareDesk ? {} : railBadges(surface, rawSubs);

  const isCurrent = (path?: string, alsoOn?: string[]) =>
    [path, ...(alsoOn ?? [])].some(
      (p) => !!p && (location.pathname === p || location.pathname.startsWith(`${p}/`)),
    );

  const legacyDoors = useMemo(
    () => (surfaceIsFallback ? items.filter((i) => !i.children?.length) : []),
    [items, surfaceIsFallback],
  );
  const legacyGroups = useMemo(
    () => (surfaceIsFallback ? items.filter((i) => i.children?.length) : []),
    [items, surfaceIsFallback],
  );

  return (
    <div className="maie desk kit-rail-dock">
      <nav className="siderail rail-fit" aria-label="Main">
        <div className="rail-brand">
          <Brand mark="type" style={{ height: 20, color: 'var(--ink-900)' }} />
        </div>

        {surfaceIsFallback ? (
          <>
            {legacyDoors.map((item) => (
              <LegacyDoor key={item.id} item={item} current={isCurrent(item.path)} />
            ))}
            {legacyGroups.map((group) => {
              const open = openGroups[group.id] ?? group.children!.some((c) => isCurrent(c.path));
              return (
                <div key={group.id}>
                  <button
                    type="button"
                    className="rail-group-head"
                    aria-expanded={open}
                    onClick={() => setOpenGroups((o) => ({ ...o, [group.id]: !open }))}
                  >
                    <p className="t-ovl mut grow">{group.label}</p>
                    <span className="t-cap mut">{open ? '▾' : '▸'}</span>
                  </button>
                  {open ? (
                    group.children!.map((c) => (
                      <LegacyDoor key={c.id} item={c} current={isCurrent(c.path)} indent />
                    ))
                  ) : (
                    <button
                      type="button"
                      className="rail-item"
                      onClick={() => setOpenGroups((o) => ({ ...o, [group.id]: true }))}
                    >
                      <span className="t-cap mut">{group.children!.length} doors, folded</span>
                    </button>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <>
            {def.doors
              .filter((door) => !door.requires || roleCheck.hasPermission(door.requires))
              /* A DOOR THAT GOES NOWHERE IS NOT DRAWN — unless nothing on this
                 surface is built yet (the three roles whose lanes have not
                 landed), where hiding every door leaves an empty column and
                 tells the next builder nothing. Then they stay, inert and
                 muted, with no tooltip explaining our backlog. */
              .filter((door) => !anyRouted || Boolean(doorRouteFor(surface, door.key) ?? door.route))
              .map((door) => (
              <DesignDoorRow
                key={door.key}
                door={
                  liveSubs[door.key] || liveBadges[door.key]
                    ? {
                        ...door,
                        sub: liveSubs[door.key] ?? door.sub,
                        badge: liveBadges[door.key] ?? door.badge,
                      }
                    : door
                }
                current={isCurrent(doorRouteFor(surface, door.key) ?? door.route, door.activeOn)}
                hasRoute={Boolean(doorRouteFor(surface, door.key) ?? door.route)}
                onGo={() => {
                  const to = doorRouteFor(surface, door.key) ?? door.route;
                  if (to) navigate(to);
                }}
              />
            ))}

            {def.groups?.map((group) => {
              /* THE HAT ONLY APPEARS ON SOMEONE WHO WEARS IT. See `requires`
                 in designRoles — an employed doctor is not the clinic's
                 manager, and five doors to Reports and System Settings that
                 all answer 403 is worse than no fold at all. */
              if (group.requires?.length && !roleCheck.hasAnyPermission(group.requires)) {
                return null;
              }
              const open = openGroups[group.head] ?? false;
              const hat = DESIGN_ROLES[group.role];
              /* AND A HAT WITH NO DOORS IS NOT A HAT. The nurse carries two
                 folds whose surfaces are not built yet, so both opened onto
                 nothing — one of them captioned "0 doors, folded". A fold is
                 drawn once there is something under it. */
              const doors = hat.doors.filter(
                (d) =>
                  (!d.requires || roleCheck.hasPermission(d.requires)) &&
                  Boolean(doorRouteFor(group.role, d.key) ?? d.route),
              );
              if (!doors.length) return null;
              return (
                <div key={group.head}>
                  <button
                    type="button"
                    className="rail-group-head"
                    aria-expanded={open}
                    onClick={() => setOpenGroups((o) => ({ ...o, [group.head]: !open }))}
                  >
                    <p className="t-ovl mut grow">{group.head}</p>
                    <span className="t-cap mut">{open ? '▾' : '▸'}</span>
                  </button>
                  {open ? (
                    /* A DOOR WITH NOWHERE TO GO IS NOT A DOOR — `doors` above
                       is already filtered to the ones that exist. */
                    doors.map((d) => (
                      <button
                        key={d.key}
                        type="button"
                        className="rail-item"
                        onClick={() => {
                          const to = doorRouteFor(group.role, d.key) ?? d.route;
                          if (to) navigate(to);
                        }}
                      >
                        <span className="t-cap mut grow" style={{ paddingLeft: 26 }}>{d.label}</span>
                        {d.badge ? <Chip kind="warn" small>{d.badge}</Chip> : null}
                      </button>
                    ))
                  ) : (
                    <button
                      type="button"
                      className="rail-item"
                      onClick={() => setOpenGroups((o) => ({ ...o, [group.head]: true }))}
                    >
                      {/* The canvas's caption opens with a count, and the fold
                          now draws only the doors that exist — so the count is
                          taken from what will actually be listed. A hat with
                          one door says "1 door"; the canvas never had to. */}
                      <span className="t-cap mut">
                        {group.literalCaption
                          ? group.caption
                          : group.caption.replace(
                              /\d+ doors/,
                              doors.length === 1 ? '1 door' : `${doors.length} doors`,
                            )}
                      </span>
                    </button>
                  )}
                </div>
              );
            })}
          </>
        )}

        <button type="button" className="rail-foot" onClick={onOpenMenu}>
          <span className="av" aria-hidden="true">{person?.initials}</span>
          <div className="grow">
            <p className="t-sec w6">{person?.name}</p>
            <p className="t-cap mut">
              {/* NO ▾ ON "All branches". The canvas draws one because it
                  depicts a branch switcher; the product has no branch model
                  yet (one clinic is one tenant), so the caret promised a
                  picker that does not exist. `branch` keeps its own ▾ — that
                  one opens the clinic switch in the account menu. */}
              {[person?.role, def.scope === 'all-branches' ? 'All branches' : branch]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <span className="mut"><Icon name="dots-vertical" /></span>
        </button>
      </nav>
    </div>
  );
}

function DesignDoorRow({
  door,
  current,
  onGo,
  hasRoute = true,
}: {
  door: DesignDoor;
  current: boolean;
  onGo: () => void;
  /** False only on a surface whose lane has not landed — see the rail above. */
  hasRoute?: boolean;
}) {
  return (
    <>
      {/* The Accountant's static overlines — Money in / Money out / The books. */}
      {door.head ? (
        <p className="t-ovl mut" style={{ padding: '14px 10px 4px' }}>{door.head}</p>
      ) : null}
      <button
        type="button"
        className={clsx('rail-item', current && 'on', door.sub && 'has-sub')}
        aria-current={current ? 'page' : undefined}
        disabled={!hasRoute}
        onClick={onGo}
      >
        {door.sub ? (
          <>
            <span className="rail-ic"><Icon name={door.icon} /></span>
            <div className="grow" style={{ minWidth: 0 }}>
              <span>{door.label}</span>
              <p className={clsx('t-cap', 'rail-sub', !current && 'mut')}>{door.sub}</p>
            </div>
            {door.badge ? <Chip kind="warn" small className="w6">{door.badge}</Chip> : null}
          </>
        ) : (
          <>
            <Icon name={door.icon} />
            <span className="grow">{door.label}</span>
          </>
        )}
      </button>
    </>
  );
}

function LegacyDoor({
  item,
  current,
  indent = false,
}: {
  item: SidebarItem;
  current: boolean;
  indent?: boolean;
}) {
  const AppIcon = item.icon;
  return (
    <NavLink
      to={item.path ?? '#'}
      className={clsx('rail-item', current && 'on')}
      aria-current={current ? 'page' : undefined}
      style={indent ? { paddingLeft: 26 } : undefined}
    >
      {AppIcon ? <AppIcon className="ic" /> : null}
      <span className="grow">{item.label}</span>
      {item.badge ? <Chip kind="warn" small className="w6">{item.badge}</Chip> : null}
    </NavLink>
  );
}
